import { z } from 'zod';

import { RequestAuditAction } from './requests.js';
import { ACTION_CODES } from './audit-action-codes.js';

// Admin-operations audit-log domain. Distinct from `AuditAction` in
// `./requests.ts`, which is the per-Request audit domain (`Request.audit[]`).
// The two namespaces never alias and intentionally use different identifier
// names so a future contributor cannot collapse them by accident.
// The action-code catalog lives in ./audit-action-codes.ts (extracted to
// keep this file under the 400-line soft limit).

/**
 * @aiPurpose typed catalog of admin-ops audit codes.
 * @aiInvariant cardinality is exactly 112 (#2863 appended three
 *   `cargoProblem.*` workspace codes — assignee change, attachment delete,
 *   reaction escalate; #2783 appended six `cargoProblem.*` codes; #1227
 *   appended 12 `cargo.*` admin-ops codes); future sub-issues append codes
 *   only — never remove or rename, since historical audit rows reference these
 *   literals forever. The cardinality lock-test is an anti-drift tripwire.
 */
export const AuditEventCodeSchema = z.enum(ACTION_CODES);
export type AuditEventCode = z.infer<typeof AuditEventCodeSchema>;
export const AUDIT_EVENT_CODES: readonly AuditEventCode[] = ACTION_CODES;

/**
 * @aiPurpose denormalised pointer to the actor User on a returned AuditEvent.
 * @aiInvariant `null` exclusively when (a) `actorUserId` is null after the
 *   `ON DELETE SET NULL` cascade, or (b) the User row is otherwise unavailable
 *   at JOIN time. Never carries a passwordHash or any non-public field.
 * @aiPii email + name are PII; consumer must respect log-redaction rules when
 *   forwarding the value off-system.
 */
export const ActorRefSchema = z
  .object({
    id: z.string().min(1),
    email: z.string().email(),
    name: z.string().min(1),
  })
  .strict()
  .nullable();
export type ActorRef = z.infer<typeof ActorRefSchema>;

const DiffEntrySchema = z.object({
  before: z.unknown(),
  after: z.unknown(),
});

// Two diff shapes coexist by design (issue #93):
//   field-keyed `Record<field, { before, after }>` — used by user/department
//   side-keyed  `{ before?: {...}, after: {...} }` — used by role.* per spec
// Both encode the same information; the spec for #93 explicitly chose
// side-keyed for role events so consumers can render permissions[] as a single
// pair instead of a 47-key Record. `role.create` carries only `after` (no
// previous state for a new entity); the other four events carry both. The
// union tries side-keyed first (.strict() rejects field-keyed payloads), then
// falls through to field-keyed.
const SideKeyedDiffSchema = z
  .object({
    before: z.unknown().optional(),
    after: z.unknown(),
  })
  .strict();

const DiffSchema = z.union([SideKeyedDiffSchema, z.record(z.string(), DiffEntrySchema)]).nullable();

/**
 * @aiPurpose input shape for `emitAudit(deps, input)` — write-side contract.
 * @aiInvariant `.strict()` rejects `id` and `at` if accidentally provided;
 *   those are server-generated (`cuid()` for `id`, `DEFAULT clock_timestamp()`
 *   for `at` — INSERT-time, not BEGIN-time, so display order stays monotonic
 *   under SELECT...FOR UPDATE contention) and never accepted from a caller.
 *   Must round-trip through Zod inside `emitAudit` so a malformed payload
 *   from a controller fails loud rather than landing a partial row in
 *   `audit_event`.
 */
export const AuditEventInputSchema = z
  .object({
    // `actorUserId` is the User.id UUID FK; an unparseable value would
    // pass through Zod and fail late inside Prisma/Postgres. Validate the
    // shape up front so emitAudit fails loud at the call boundary.
    actorUserId: z.string().uuid().nullable(),
    action: AuditEventCodeSchema,
    targetType: z.string().min(1),
    // `targetId` intentionally stays a free string — targets include both
    // UUID users and cuid departments / future role-permission ids.
    targetId: z.string().min(1).nullable(),
    reqId: z.string().min(1).nullable(),
    diff: DiffSchema,
    reason: z.string().min(1).nullable(),
  })
  .strict();
export type AuditEventInput = z.infer<typeof AuditEventInputSchema>;

/**
 * @aiPurpose read shape returned by `GET /api/audit` and (for tracing) by
 *   `emitAudit`. Strict superset of the input shape: server-generated `id`,
 *   `at`, and the JOIN-denormalised `actor` reference.
 * @aiInvariant `at` is ISO-8601 with timezone; sort order on `(at DESC, id DESC)`
 *   is stable because `id` is a cuid (lexicographically increasing) — collisions
 *   on `at` are tiebroken deterministically.
 */
export const AuditEventSchema = AuditEventInputSchema.extend({
  id: z.string().min(1),
  at: z.string().datetime({ offset: true }),
  actor: ActorRefSchema,
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

/**
 * @aiPurpose query string for `GET /api/audit` — cursor-paginated admin list.
 * @aiInvariant `.strict()` blocks unknown keys; `limit` clamped to 1..100 with
 *   default 50; `cursor` is opaque base64url(`{at, id}`) decoded server-side.
 *   `dateTo` is interpreted as exclusive (`at < dateTo`) for half-open ranges.
 */
export const GetAuditQuerySchema = z
  .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    actorUserId: z.string().uuid().optional(),
    action: AuditEventCodeSchema.optional(),
    targetType: z.string().min(1).optional(),
    targetId: z.string().min(1).optional(),
    dateFrom: z.string().datetime({ offset: true }).optional(),
    dateTo: z.string().datetime({ offset: true }).optional(),
  })
  .strict();
export type GetAuditQuery = z.infer<typeof GetAuditQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/audit`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying query
 *   returned ≤ `limit` rows; otherwise it carries the encoded `(at, id)` of
 *   the last returned row.
 */
export const GetAuditResponseSchema = z
  .object({
    items: z.array(AuditEventSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetAuditResponse = z.infer<typeof GetAuditResponseSchema>;

/**
 * @aiPurpose wire shape for one row of `RequestAuditEntry` returned by
 *   `GET /api/audit/request/:id`. Distinct from the legacy per-request
 *   drawer `AuditEntrySchema` (which renders inline copy via `text`/`by`)
 *   — this is the raw forensic row admins read after a Request hard-delete.
 * @aiInvariant `requestId` is null exactly when the parent Request was
 *   hard-deleted (`ON DELETE SET NULL`). The denormalised
 *   `payload.requestId` (maintained by a BEFORE INSERT/UPDATE trigger)
 *   always carries the canonical id and survives the FK cascade —
 *   that's the key the orphan-recovery query matches on.
 * @aiPii `actor.{email,name}` is PII; consumer must respect log-redaction
 *   rules when forwarding off-system. Same contract as `AuditEvent.actor`.
 */
export const RequestAuditEntryRowSchema = z
  .object({
    id: z.string().min(1),
    requestId: z.string().min(1).nullable(),
    action: z.nativeEnum(RequestAuditAction),
    payload: z.unknown().nullable(),
    actor: ActorRefSchema,
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type RequestAuditEntryRow = z.infer<typeof RequestAuditEntryRowSchema>;

/**
 * @aiPurpose query string for `GET /api/audit/request/:id` — optional
 *   per-stream cursor pagination. A truncated stream (>= 500 rows in
 *   the page) carries a non-null `*NextCursor` that the operator can
 *   pass back as `*Cursor` to fetch the next 500.
 * @aiInvariant `.strict()` blocks unknown keys. The two cursors are
 *   independent — a forensic-recovery operator can paginate the audit
 *   feed without paginating the ledger and vice versa. Cursors are
 *   opaque base64url tuples; the server-side decoders enforce the
 *   per-stream shape (`{at,id}` vs `{createdAt,id}`) so a misrouted
 *   cursor (cursor B in slot A) fails decode rather than silently
 *   mis-paginates.
 * @aiInvariant The walk is a strict descending traversal past the
 *   prior page's cursor. Each HTTP call opens a fresh
 *   `RepeatableRead` snapshot, but the cursor predicates filter out
 *   rows with `at`/`createdAt` NEWER than the cursor — so an
 *   AuditEvent or ledger entry committed BETWEEN pages does not
 *   surface in the walk. To capture inter-page rows the operator
 *   refreshes page 1. The historical tail past the cursor is
 *   immutable: both timestamp columns default to `clock_timestamp()`
 *   (NOT `now()`/`transaction_timestamp()`), so a long-running
 *   mutation transaction that begins before page 1 but commits after
 *   it lands its row with INSERT-time, not BEGIN-time, and the cursor
 *   predicate keeps it out of the descending walk. The walk therefore
 *   converges to a stable set. `exists` is recomputed each call so a
 *   hard-delete committed between pages flips it on the next response.
 */
export const GetRequestAuditQuerySchema = z
  .object({
    auditEventsCursor: z.string().min(1).optional(),
    ledgerEntriesCursor: z.string().min(1).optional(),
  })
  .strict();
export type GetRequestAuditQuery = z.infer<typeof GetRequestAuditQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/audit/request/:id` — the
 *   admin forensic-recovery view that surfaces both ledger streams for
 *   a request id, including rows orphaned by a hard-delete of the parent.
 * @aiInvariant Both arrays sort newest-first. `auditEvents` filters on
 *   `targetType='request' AND targetId=:id` (no FK, always reachable);
 *   `ledgerEntries` filters on `requestId=:id OR (requestId IS NULL AND
 *   payload->>'requestId'=:id)` so survivors of a SET NULL cascade
 *   surface alongside live attached rows. `exists` reflects whether the
 *   parent Request row currently exists at query time.
 * @aiInvariant Per-stream cursor pagination: `*Truncated=true` iff
 *   `*NextCursor !== null` (both encode the same fact — "the page hit
 *   the 500-row cap"). Both flags AND both cursors are kept on the
 *   response shape so a forensic-recovery UI can render either:
 *   - the boolean alone ("history truncated, contact ops"), or
 *   - the cursor ("load older rows" link).
 *   `.strict()` is preserved — adding required fields is safe because
 *   no web/shared-hooks consumer of this response shape exists today
 *   (verified by grep), so the only consumer that breaks under the
 *   wider response is the e2e test, which is updated in this PR.
 */
export const GetRequestAuditResponseSchema = z
  .object({
    requestId: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
    exists: z.boolean(),
    auditEvents: z.array(AuditEventSchema),
    auditEventsTruncated: z.boolean(),
    auditEventsNextCursor: z.string().min(1).nullable(),
    ledgerEntries: z.array(RequestAuditEntryRowSchema),
    ledgerEntriesTruncated: z.boolean(),
    ledgerEntriesNextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetRequestAuditResponse = z.infer<typeof GetRequestAuditResponseSchema>;
